import React, { useState } from 'react';
import { Trash2, Users2, X, MessageSquareX } from 'lucide-react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { useAppDispatch, useAppSelector } from '../../hooks/hooks';
import {
  clearConversationSelection,
  performBulkAction,
  selectSelectedConversationIds,
  selectBulkActionStatus,
  fetchConversations
} from '../../store/slices/conversationsSlice';
import { toast } from 'react-hot-toast';
import { Logger } from '../../utils/logger';
import { hasFullAdminAccess } from '../../utils/roleUtils';

interface ConversationBulkActionsBarProps {
  selectedCount: number;
}

interface Employee {
  value: string;
  label: string;
}

interface ConfirmationState {
  isOpen: boolean;
  action: string;
  message: string;
  selectedEmployee?: string;
}

const ActionBar = styled.div`
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  width: fit-content;
  background: #ffffff;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  border-radius: 16px;
  padding: 16px 24px;
  display: flex;
  align-items: center;
  gap: 24px;
  border: 1px solid #f0f0f0;
  z-index: 1000;
`;

const SelectionBadge = styled.div`
  background: rgba(37, 99, 235, 0.1);
  color: #2563eb;
  padding: 8px 16px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  min-width: 100px;
  text-align: center;
  font-family: 'Gilroy-Medium', sans-serif;
`;

const ActionGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding-left: 16px;
  border-left: 1px solid #f0f0f0;
`;

const ActionButton = styled.button<{ $danger?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 8px;
  border-radius: 8px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: ${props => props.$danger ? '#ef4444' : '#64748b'};
  transition: all 0.2s;
  min-width: 64px;
  font-family: 'Gilroy-Medium', sans-serif;

  &:hover {
    background: ${props => props.$danger ? '#fef2f2' : '#f8fafc'};
    color: ${props => props.$danger ? '#dc2626' : '#2563eb'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    width: 20px;
    height: 20px;
  }

  span {
    font-size: 12px;
    font-weight: 500;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
`;

const SelectWrapper = styled.div`
  margin-bottom: 24px;
  select {
    width: 100%;
    height: 40px;
    padding: 8px;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
    font-family: 'Gilroy-Medium', sans-serif;
  }
`;

const ModalContent = styled.div`
  position: relative;
  z-index: 1101;
  background: white;
  border-radius: 16px;
  padding: 24px;
  width: 400px;
  max-width: 90vw;
`;

const ModalTitle = styled.h3`
  margin: 0 0 16px;
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  font-family: 'Gilroy-SemiBold', sans-serif;
`;

const ModalMessage = styled.p`
  margin: 0 0 24px;
  color: #64748b;
  font-size: 14px;
  line-height: 1.5;
  font-family: 'Gilroy-Medium', sans-serif;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const Button = styled.button<{ $isWarning?: boolean }>`
  padding: 8px 16px;
  border-radius: 8px;
  border: none;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  font-family: 'Gilroy-Medium', sans-serif;

  ${props => props.$isWarning ? `
    background: #ef4444;
    color: white;
    &:hover { background: #dc2626; }
  ` : `
    background: #f1f5f9;
    color: #64748b;
    &:hover { background: #e2e8f0; }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const getActionTitle = (action: string): string => {
  switch (action) {
    case 'delete_conversation': return 'Delete Conversation';
    case 'delete_chat': return 'Delete Chat';
    case 'assign': return 'Assign';
    default: return 'Confirm';
  }
};

const getConfirmationMessage = (action: string, count: number): string => {
  switch (action) {
    case 'delete_conversation':
      return `Are you sure you want to delete ${count} conversation${count > 1 ? 's' : ''}? This will remove the conversation from your inbox but keep the chat history.`;
    case 'delete_chat':
      return `Are you sure you want to delete ${count} chat${count > 1 ? 's' : ''} completely? This action cannot be undone and will remove all messages.`;
    case 'assign':
      return `Select a team member to assign ${count} conversation${count > 1 ? 's' : ''} to:`;
    default:
      return 'Are you sure you want to continue?';
  }
};

const ConversationBulkActionsBar: React.FC<ConversationBulkActionsBarProps> = ({
  selectedCount
}) => {
  const dispatch = useAppDispatch();
  const selectedIds = useAppSelector(selectSelectedConversationIds);
  const bulkActionStatus = useAppSelector(selectBulkActionStatus);
  const isLoading = bulkActionStatus === 'loading';

  const [confirmation, setConfirmation] = useState<ConfirmationState>({
    isOpen: false,
    action: '',
    message: ''
  });

  // Get employee list for assignment
  const formatName = (firstName: string, lastName: string | null) => {
    if (!firstName) return '';
    return lastName ? `${firstName} ${lastName}`.trim() : firstName.trim();
  };

  const employees: Employee[] = useSelector((state: any) => {
    const userRole = state?.rootReducers?.userData?.userDetails?.role?.name;
    const subordinateData = state?.rootReducers?.userData?.userDetails?.subordinateEmployees;
    const userDetails = state?.rootReducers?.userData?.userDetails;

    if (hasFullAdminAccess(userDetails)) {
      const orgEmployees = userDetails?.organizationEmployee || [];
      return orgEmployees.map((item: any) => ({
        value: item?._id,
        label: formatName(item?.firstName, item?.lastName)
      }));
    }

    const reportTo = subordinateData?.reportTo || [];
    const teamMembers = subordinateData?.teamMembers || [];
    const orgMembers = subordinateData?.organizationMember || [];

    let filteredUsers: any[] = [];

    if (userRole === 'employee') {
      const currentUser = {
        firstName: userDetails?.firstName || '',
        lastName: userDetails?.lastName || '',
        _id: userDetails?._id,
      };
      filteredUsers = [currentUser];
    } else if (userRole === 'manager' || userRole === 'team_lead') {
      const allSubordinates = [...reportTo, ...teamMembers];
      filteredUsers = allSubordinates.filter((user, index, array) =>
        array.findIndex(u => u._id === user._id) === index
      );
    } else {
      const allUsers = [...reportTo, ...teamMembers, ...orgMembers];
      filteredUsers = allUsers.filter((user, index, array) =>
        array.findIndex(u => u._id === user._id) === index
      );
    }

    return filteredUsers.map((item: any) => ({
      value: item?._id,
      label: formatName(item?.firstName, item?.lastName)
    }));
  });

  const handleActionClick = (action: string) => {
    setConfirmation({
      isOpen: true,
      action,
      message: getConfirmationMessage(action, selectedCount)
    });
  };

  const handleClearSelection = () => {
    dispatch(clearConversationSelection());
  };

  const handleConfirm = async () => {
    const { action, selectedEmployee } = confirmation;

    try {
      Logger.log('Performing bulk action:', { action, selectedIds, selectedEmployee });

      const payload: any = {
        action: action as any,
        conversationIds: selectedIds,
      };

      if (action === 'assign' && selectedEmployee) {
        payload.assignToIds = [selectedEmployee];
      }

      await dispatch(performBulkAction(payload)).unwrap();

      toast.success(`Successfully ${getActionTitle(action).toLowerCase()}ed ${selectedCount} conversation${selectedCount > 1 ? 's' : ''}`);

      // Refresh conversations after bulk action
      dispatch(fetchConversations({ page: 1, refresh: true }));

    } catch (error: any) {
      Logger.log('Bulk action error:', error);
      toast.error(error || 'Failed to perform action');
    }

    setConfirmation({
      isOpen: false,
      action: '',
      message: '',
      selectedEmployee: undefined
    });
  };

  if (selectedCount === 0) return null;

  return (
    <>
      <ActionBar>
        <SelectionBadge>
          {selectedCount} selected
        </SelectionBadge>

        <ActionGroup>
          <ActionButton
            onClick={() => handleActionClick('assign')}
            title="Assign conversations to team member"
            disabled={isLoading}
          >
            <Users2 />
            <span>Assign</span>
          </ActionButton>

          <ActionButton
            onClick={() => handleActionClick('delete_conversation')}
            title="Remove from inbox (keeps chat history)"
            disabled={isLoading}
            $danger
          >
            <Trash2 />
            <span>Delete</span>
          </ActionButton>

          <ActionButton
            onClick={() => handleActionClick('delete_chat')}
            title="Delete completely (removes all messages)"
            disabled={isLoading}
            $danger
          >
            <MessageSquareX />
            <span>Erase</span>
          </ActionButton>

          <ActionButton
            onClick={handleClearSelection}
            title="Clear selection"
            disabled={isLoading}
          >
            <X />
            <span>Clear</span>
          </ActionButton>
        </ActionGroup>
      </ActionBar>

      {confirmation.isOpen && (
        <ModalOverlay onClick={() => !isLoading && setConfirmation({ isOpen: false, action: '', message: '' })}>
          <ModalContent onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            <ModalTitle>{getActionTitle(confirmation.action)}</ModalTitle>
            <ModalMessage>{confirmation.message}</ModalMessage>

            {/* Assignment Select */}
            {confirmation.action === 'assign' && (
              <SelectWrapper>
                <select
                  value={confirmation.selectedEmployee || ''}
                  onChange={(e) => setConfirmation(prev => ({
                    ...prev,
                    selectedEmployee: e.target.value
                  }))}
                  className="form-select"
                  disabled={isLoading}
                >
                  <option value="">Select Team Member</option>
                  {employees.map((emp: Employee) => (
                    <option key={emp.value} value={emp.value}>
                      {emp.label}
                    </option>
                  ))}
                </select>
              </SelectWrapper>
            )}

            <ModalActions>
              <Button
                onClick={() => setConfirmation({
                  isOpen: false,
                  action: '',
                  message: '',
                  selectedEmployee: undefined
                })}
                disabled={isLoading}
              >
                Cancel
              </Button>

              <Button
                $isWarning={confirmation.action === 'delete_conversation' || confirmation.action === 'delete_chat'}
                disabled={
                  isLoading ||
                  (confirmation.action === 'assign' && !confirmation.selectedEmployee)
                }
                onClick={handleConfirm}
              >
                {isLoading ? 'Processing...' : getActionTitle(confirmation.action)}
              </Button>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}
    </>
  );
};

export default ConversationBulkActionsBar;
